import scaleJson from "../content/scale.json";
import acceptatieJson from "../content/themes/acceptatie.json";
import defusieJson from "../content/themes/defusie.json";
import zelfAlsContextJson from "../content/themes/zelf-als-context.json";
import hierEnNuJson from "../content/themes/hier-en-nu.json";
import waardenJson from "../content/themes/waarden.json";
import waardengerichtHandelenJson from "../content/themes/waardengericht-handelen.json";
import type {
  BloomQuestion,
  BloomQuestionWithTheme,
  BloomScale,
  BloomThemeFile,
  BloomThemeMeta,
} from "../types/bloom-content";

const THEME_FILES: BloomThemeFile[] = [
  acceptatieJson as BloomThemeFile,
  defusieJson as BloomThemeFile,
  zelfAlsContextJson as BloomThemeFile,
  hierEnNuJson as BloomThemeFile,
  waardenJson as BloomThemeFile,
  waardengerichtHandelenJson as BloomThemeFile,
];

const EXPECTED_THEME_COUNT = 6;
const LIKERT_PER_THEME = 4;
const SCENARIO_PER_THEME = 2;
const QUESTIONS_PER_THEME = LIKERT_PER_THEME + SCENARIO_PER_THEME;
const LIKERT_SLOTS = [1, 2, 3, 4];
const SCENARIO_SLOTS = [5, 6];

export const bloomScale = scaleJson as BloomScale;

export function isScenarioQuestion(q: BloomQuestion): boolean {
  return q.kind === "scenario";
}

function validateLikertQuestion(q: BloomQuestion): void {
  if (!q.text?.trim()) {
    throw new Error(`Likert question ${q.id} missing text`);
  }
  if (q.situation || q.prompt || q.options?.length) {
    throw new Error(`Likert question ${q.id} must not have scenario fields`);
  }
}

function validateScenarioQuestion(q: BloomQuestion): void {
  if (!q.situation?.trim()) {
    throw new Error(`Scenario question ${q.id} missing situation`);
  }
  if (!q.prompt?.trim()) {
    throw new Error(`Scenario question ${q.id} missing prompt`);
  }
  if (q.reversed) {
    throw new Error(`Scenario question ${q.id} must not have reversed`);
  }
  const options = q.options;
  if (!options || options.length !== 5) {
    throw new Error(`Scenario question ${q.id} must have exactly 5 options`);
  }
  for (let i = 0; i < 5; i++) {
    if (!options[i].text?.trim()) {
      throw new Error(`Scenario question ${q.id} option ${i} missing text`);
    }
    if (options[i].score !== i) {
      throw new Error(`Scenario question ${q.id} option ${i} score must be ${i}`);
    }
  }
}

function validateThemeQuestions(themeId: string, questions: BloomQuestion[]): void {
  const likerts = questions.filter((q) => !isScenarioQuestion(q));
  const scenarios = questions.filter((q) => isScenarioQuestion(q));

  if (likerts.length !== LIKERT_PER_THEME) {
    throw new Error(
      `Theme "${themeId}" has ${likerts.length} Likert questions (expected ${LIKERT_PER_THEME})`
    );
  }
  if (scenarios.length !== SCENARIO_PER_THEME) {
    throw new Error(
      `Theme "${themeId}" has ${scenarios.length} scenario questions (expected ${SCENARIO_PER_THEME})`
    );
  }

  for (const q of likerts) {
    validateLikertQuestion(q);
    const slot = itemIndexFromQuestionId(q.id);
    if (!LIKERT_SLOTS.includes(slot)) {
      throw new Error(`Likert question ${q.id} must use q1–q4`);
    }
  }

  for (const q of scenarios) {
    validateScenarioQuestion(q);
    const slot = itemIndexFromQuestionId(q.id);
    if (!SCENARIO_SLOTS.includes(slot)) {
      throw new Error(`Scenario question ${q.id} must use q5 or q6`);
    }
  }
}

function validateContent(): void {
  if (THEME_FILES.length !== EXPECTED_THEME_COUNT) {
    throw new Error(`Expected ${EXPECTED_THEME_COUNT} themes, got ${THEME_FILES.length}`);
  }

  const orders = new Set<number>();
  const ids = new Set<string>();

  for (const file of THEME_FILES) {
    validateThemeQuestions(file.theme.id, file.questions);

    for (const q of file.questions) {
      if (orders.has(q.order)) {
        throw new Error(`Duplicate question order: ${q.order}`);
      }
      orders.add(q.order);

      if (ids.has(q.id)) {
        throw new Error(`Duplicate question id: ${q.id}`);
      }
      ids.add(q.id);
    }
  }

  const totalQuestions = THEME_FILES.reduce((n, f) => n + f.questions.length, 0);
  const expectedTotal = EXPECTED_THEME_COUNT * QUESTIONS_PER_THEME;
  if (orders.size !== totalQuestions || totalQuestions !== expectedTotal) {
    throw new Error(
      `Expected ${expectedTotal} unique orders 1–${expectedTotal}, got ${orders.size}`
    );
  }

  for (let i = 1; i <= expectedTotal; i++) {
    if (!orders.has(i)) {
      throw new Error(`Missing question order: ${i}`);
    }
  }

  if (bloomScale.labels.length !== bloomScale.max - bloomScale.min + 1) {
    throw new Error("Scale labels count must match min–max range");
  }
}

validateContent();

export const bloomThemes: BloomThemeMeta[] = THEME_FILES.map((f) => f.theme);

export const bloomQuestions: BloomQuestionWithTheme[] = THEME_FILES.flatMap((file) =>
  file.questions.map((q) => ({
    ...q,
    themeId: file.theme.id,
  }))
).sort((a, b) => a.order - b.order);

export function getQuestionsByTheme(themeId: string): BloomQuestionWithTheme[] {
  return bloomQuestions.filter((q) => q.themeId === themeId);
}

export const BLOOM_QUESTION_COUNT = bloomQuestions.length;
export const BLOOM_LIKERT_COUNT = bloomQuestions.filter((q) => !isScenarioQuestion(q)).length;

function itemIndexFromQuestionId(id: string): number {
  const match = id.match(/-q(\d+)$/);
  if (!match) throw new Error(`Invalid question id: ${id}`);
  return Number(match[1]);
}

function shuffleInPlace<T>(items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}

function shuffleQuestionGroup(
  questions: BloomQuestionWithTheme[],
  slots: number[]
): BloomQuestionWithTheme[] {
  const blocks: BloomQuestionWithTheme[][] = slots.map(() => []);
  for (const question of questions) {
    const slot = itemIndexFromQuestionId(question.id);
    const blockIndex = slots.indexOf(slot);
    if (blockIndex === -1) {
      throw new Error(`Question ${question.id} does not belong in this group`);
    }
    blocks[blockIndex].push(question);
  }
  if (blocks.some((block) => block.length !== EXPECTED_THEME_COUNT)) {
    throw new Error("Each block must contain one question per theme");
  }
  return blocks.flatMap((block) => shuffleInPlace([...block]));
}

/** Eerst alle Likert-vragen (geshuffeld), daarna alle scenario-vragen (geshuffeld). */
export function shuffledBloomQuestions(): BloomQuestionWithTheme[] {
  const likert = bloomQuestions.filter((q) => !isScenarioQuestion(q));
  const scenarios = bloomQuestions.filter((q) => isScenarioQuestion(q));
  return [
    ...shuffleQuestionGroup(likert, LIKERT_SLOTS),
    ...shuffleQuestionGroup(scenarios, SCENARIO_SLOTS),
  ];
}
